import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Badge,
  Button,
  Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { FeedbackService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";

interface FeedbackDetail {
  id: number;
  message: string;
  isResolved: boolean;
  adminReply: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  submittedBy: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
  };
}

interface ReplyForm {
  reply: string;
}

const roleMap: Record<string, { label: string; color: string }> = {
  ROLE_COMPANY_ADMIN: { label: "Company Admin", color: "primary" },
  ROLE_COMPANY_USER: { label: "Company User", color: "info" },
  ROLE_USER: { label: "Coach", color: "success" },
};

const FeedbackDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [feedback, setFeedback] = useState<FeedbackDetail | null>(null);
  const [loadingResolve, setLoadingResolve] = useState(false);
  const [loadingReply, setLoadingReply] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReplyForm>();

  const fetchFeedback = () => {
    if (!id) return;
    FeedbackService.getById(Number(id))
      .then((res: any) => setFeedback(res?.data ?? res))
      .catch(ErrorToast);
  };

  useEffect(() => {
    fetchFeedback();
  }, [id]);

  const handleToggleResolve = async () => {
    if (!feedback) return;
    setLoadingResolve(true);
    try {
      if (feedback.isResolved) {
        await FeedbackService.unresolve(feedback.id);
        SuccessToast(t("feedback.markedUnresolved", "Marked as unresolved"));
      } else {
        await FeedbackService.resolve(feedback.id);
        SuccessToast(t("feedback.markedResolved", "Marked as resolved"));
      }
      fetchFeedback();
    } catch (err) {
      ErrorToast(err);
    } finally {
      setLoadingResolve(false);
    }
  };

  const onSubmitReply = async (data: ReplyForm) => {
    if (!feedback) return;
    setLoadingReply(true);
    try {
      await FeedbackService.reply(feedback.id, { reply: data.reply });
      SuccessToast(t("feedback.replySent", "Reply sent successfully"));
      reset();
      fetchFeedback();
    } catch (err) {
      ErrorToast(err);
    } finally {
      setLoadingReply(false);
    }
  };

  const getUserName = (user: FeedbackDetail["submittedBy"] | undefined) => {
    if (!user) return "-";
    return [user.firstName, user.lastName].filter(Boolean).join(" ") || "-";
  };

  const getRoleBadge = (user: FeedbackDetail["submittedBy"] | undefined) => {
    if (!user?.roles?.length) return "-";
    const badge = roleMap[user.roles[0]];
    if (!badge) return user.roles[0];
    return <Badge bg={badge.color}>{badge.label}</Badge>;
  };

  if (!feedback) {
    return (
      <Col>
        <Card>
          <CardBody className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <React.Fragment>
      <Col>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/feedbacks")}
          >
            <i className="ti ti-arrow-left me-1" />
            {t("feedback.backToList", "Back to list")}
          </Button>

          <Button
            variant={feedback.isResolved ? "outline-warning" : "outline-success"}
            size="sm"
            onClick={handleToggleResolve}
            disabled={loadingResolve}
          >
            {loadingResolve ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : feedback.isResolved ? (
              <i className="ti ti-refresh me-1" />
            ) : (
              <i className="ti ti-check me-1" />
            )}
            {feedback.isResolved
              ? t("feedback.markUnresolved", "Mark as unresolved")
              : t("feedback.markResolved", "Mark as resolved")}
          </Button>
        </div>

        {/* Feedback Info */}
        <Card className="mb-4">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0">
                {t("feedback.feedbackDetails", "Feedback Details")}
              </h5>
              <Badge bg={feedback.isResolved ? "success" : "secondary"}>
                {feedback.isResolved
                  ? t("feedback.resolved", "Resolved")
                  : t("feedback.open", "Open")}
              </Badge>
            </div>

            <table className="table table-bordered table-sm">
              <tbody>
                <tr>
                  <th style={{ width: "25%" }}>{t("feedback.id", "ID")}</th>
                  <td>{feedback.id}</td>
                </tr>
                <tr>
                  <th>{t("feedback.submittedBy", "Submitted by")}</th>
                  <td>{getUserName(feedback.submittedBy)}</td>
                </tr>
                <tr>
                  <th>{t("feedback.email", "Email")}</th>
                  <td>
                    <a href={`mailto:${feedback.submittedBy?.email}`}>
                      {feedback.submittedBy?.email ?? "-"}
                    </a>
                  </td>
                </tr>
                <tr>
                  <th>{t("feedback.role", "Role")}</th>
                  <td>{getRoleBadge(feedback.submittedBy)}</td>
                </tr>
                <tr>
                  <th>{t("feedback.createdAt", "Date")}</th>
                  <td>{new Date(feedback.createdAt).toLocaleString()}</td>
                </tr>
                {feedback.resolvedAt && (
                  <tr>
                    <th>{t("feedback.resolvedAt", "Resolved at")}</th>
                    <td>{new Date(feedback.resolvedAt).toLocaleString()}</td>
                  </tr>
                )}
                <tr>
                  <th>{t("feedback.message", "Message")}</th>
                  <td style={{ whiteSpace: "pre-wrap" }}>{feedback.message}</td>
                </tr>
                {feedback.adminReply && (
                  <tr>
                    <th>{t("feedback.adminReply", "Admin reply")}</th>
                    <td>
                      <div
                        style={{
                          borderLeft: "4px solid #4f46e5",
                          paddingLeft: "12px",
                          whiteSpace: "pre-wrap",
                          color: "#374151",
                        }}
                      >
                        {feedback.adminReply}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>

        {/* Reply form / already replied notice */}
        <Card>
          <CardBody>
            <h5 className="mb-3">
              {t("feedback.replyToUser", "Reply to user")}
            </h5>

            {feedback.adminReply ? (
              <div className="alert alert-success d-flex align-items-start gap-2 mb-0">
                <i className="ti ti-circle-check fs-5 mt-1 flex-shrink-0" />
                <div>
                  <strong>{t("feedback.alreadyReplied", "Reply already sent")}</strong>
                  <p className="mb-0 mt-1 text-muted small">
                    {t("feedback.alreadyRepliedNote", "A reply has already been sent to this user. Only one reply per feedback is allowed.")}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-muted small mb-3">
                  {t(
                    "feedback.replyNote",
                    "An email will be sent to {{email}} and the feedback will be marked as resolved.",
                    { email: feedback.submittedBy?.email }
                  )}
                </p>

                <form onSubmit={handleSubmit(onSubmitReply)}>
                  <div className="mb-3">
                    <label className="form-label">
                      {t("feedback.reply", "Reply message")}
                    </label>
                    <textarea
                      className={`form-control ${errors.reply ? "is-invalid" : ""}`}
                      rows={5}
                      placeholder={t(
                        "feedback.replyPlaceholder",
                        "Write your reply here..."
                      )}
                      {...register("reply", {
                        required: t(
                          "feedback.replyRequired",
                          "Reply message is required"
                        ),
                        minLength: {
                          value: 5,
                          message: t(
                            "feedback.replyMinLength",
                            "Reply must be at least 5 characters"
                          ),
                        },
                      })}
                    />
                    {errors.reply && (
                      <div className="invalid-feedback">{errors.reply.message}</div>
                    )}
                  </div>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loadingReply}
                    >
                      {loadingReply ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t("feedback.sending", "Sending...")}
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send me-1" />
                          {t("feedback.sendReply", "Send reply")}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default FeedbackDetail;
